import { LocationContact } from "./types";
import { normalizeIdentityEmail, trimmedOrEmpty } from "../utils";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (email?: string | null): boolean => {
  const value = normalizeIdentityEmail(email);
  if (!value) return false;
  return EMAIL_REGEX.test(value);
};

export const isValidPhone = (phone?: string | null): boolean => {
  if (!phone?.trim()) return true;
  const digits = phone.replace(/\D/g, "");
  return /^\+?[\d\s().-]+$/.test(phone.trim()) && (digits.length === 10 || (digits.length === 11 && digits.startsWith("1")));
};

export const createEmptyContact = (): LocationContact => ({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  role: undefined,
});

export const hasContactData = (c?: LocationContact): boolean =>
  Boolean(
    c &&
      ((c.firstName && c.firstName.trim()) ||
        (c.lastName && c.lastName.trim()) ||
        (c.email && c.email.trim()) ||
        (c.phone && c.phone.trim()) ||
        c.role ||
        c.userId)
  );

export const hasRequiredContactFields = (c?: LocationContact): boolean => {
  if (!c) return false;
  const first = (c.firstName || "").trim();
  const email = (c.email || "").trim();
  const role = (c.role || "").trim();
  return Boolean(first && email && role && isValidEmail(email) && isValidPhone(c.phone));
};

export type ContactEntryState = {
  first: string;
  last: string;
  email: string;
  phone: string;
  role: string;
  isDraft: boolean;
  hasEntered: boolean;
  missingRequired: boolean;
  requireFirst: boolean;
  requireEmail: boolean;
  requirePhone: boolean;
  requireRole: boolean;
};

export function buildContactEntryState(
  contact: LocationContact,
  opts: { editable: boolean; showErrors: boolean }
): ContactEntryState {
  const first = trimmedOrEmpty(contact.firstName);
  const last = trimmedOrEmpty(contact.lastName);
  const email = trimmedOrEmpty(contact.email);
  const phone = trimmedOrEmpty(contact.phone);
  const role = (contact.role || "").trim();
  const isDraft = !contact.id && !contact.userId;
  const hasEntered = Boolean(
    first || last || email || phone || role || contact.id || contact.userId
  );
  const missingRequired =
    opts.editable && isDraft && hasEntered && !hasRequiredContactFields(contact);
  const shouldFlag = opts.showErrors && missingRequired;
  return {
    first,
    last,
    email,
    phone,
    role,
    isDraft,
    hasEntered,
    missingRequired,
    requireFirst: shouldFlag && !first,
    requireEmail: shouldFlag && (!email || !isValidEmail(email)),
    requirePhone: shouldFlag && !isValidPhone(phone),
    requireRole: shouldFlag && !role,
  };
}

export function getContactStatusLabel(contact: LocationContact): string {
  if (contact.inviteStatus) return contact.inviteStatus;
  return "Not invited";
}

export const removeContactAt = (
  contacts: LocationContact[],
  idx: number
): LocationContact[] => {
  const next = contacts.filter((_, i) => i !== idx);
  return next;
};

// revoke/restore removed: contacts are removed permanently

/** Text representation shared by lead contacts, email recipients, and conversion. */
export function contactPointText(points: ReadonlyArray<{ type: string; value: string }>, type: "email" | "phone"): string {
  const values: string[] = [];
  for (const point of points) if (point.type === type) values.push(point.value);
  return values.join("\n");
}
